<script>
import RadioLine from '@/components/molecules/input/RadioLine'
import Box from '@/components/atoms/Box'
import GroupOrderBlock from '@/components/organism/block/GroupOrderBlock'
import ExpenseCodeBlock from '@/components/organism/checkout/ExpenseCodeBlock'
import ComplementaryPaymentBlock from '@/components/organism/checkout/ComplementaryPaymentBlock'
import allowanceTimeFrame from '@/modules/mixins/allowanceTimeFrame'
import cloneDeep from 'lodash.clonedeep'

export default {
    name: 'ExpenseRuleBlock',
    components: {
        ComplementaryPaymentBlock,
        ExpenseCodeBlock,
        GroupOrderBlock,
        Box,
        RadioLine,
    },
    mixins: [allowanceTimeFrame],
    props: {
        value: null,
        disabled: Boolean,
        stripe: {
            type: Object,
            default: () => {},
        },
    },
    computed: {
        url() {
            return this.model ? `/api/v1/company-expense-rules/${this.model.id}` : null
        },
        currency() {
            return this.$store.getters['active/currency']
        },
        companyAddressesText() {
            return this.$addresses.items
                .filter((address) => address.is_company_address)
                .reduce((p, c, index) => {
                    if (index) p = p.concat(';<br>')
                    return p.concat(c.name)
                }, '')
        },
        timeFramesText() {
            return this.timeFrames.reduce((p, c, index) => {
                if (index) p = p.concat(';<br>')
                return p.concat(`${c.days}: ${c.time}`)
            }, '')
        },
        cart() {
            return this.$cart
        },
        priceSumUser() {
            return this.cart.calculated ? this.cart.calculation.price_sum_user : 0
        },
        priceSum() {
            return this.cart.calculated ? this.cart.calculation.price_sum : 0
        },
        active() {
            return this.url === this.value
        },
        allowance() {
            return this.$store.getters['allowance']
        },
        selectedExpenseRule() {
            return this.cart.calculation.selected_expense_rule
        },
        showExpenseCodes() {
            return Boolean(
                this.active &&
                    this.selectedExpenseRule &&
                    ((this.selectedExpenseRule.id === this.model?.id &&
                        this.selectedExpenseRule.expense_code_required &&
                        (this.selectedExpenseRule.expense_code_list ||
                            !this.selectedExpenseRule.expense_code_only_from_list)) ||
                        this.isStripe)
            )
        },
        showGroupSelector() {
            return (
                this.active &&
                this.allowance?.can_group_order &&
                this.allowance.group_order_available
            )
        },
        showPaymentExtension() {
            return (
                this.active &&
                this.model &&
                this.selectedExpenseRule?.id === this.model.id &&
                this.priceSumUser > 0
            )
        },
        addressUnavailable() {
            return this.model.address_unavailable
                ? `<strong>${this.model.address_unavailable}</strong><br>${this.companyAddressesText}<br>`
                : ''
        },
        deliveryUnavailable() {
            return this.model.delivery_unavailable
                ? `<strong>${this.model.delivery_unavailable}</strong><br>${this.timeFramesText}<br>`
                : ''
        },
        restaurantUnavailable() {
            return this.model.restaurant_unavailable
                ? `<strong>${this.model.delivery_unavailable}</strong><br>`
                : ''
        },
        information() {
            return this.disabled
                ? this.model.delivery_unavailable ||
                  this.model.restaurant_unavailable ||
                  this.model.address_unavailable
                    ? `${this.addressUnavailable}${this.deliveryUnavailable}${this.restaurantUnavailable}`
                    : this.$t('labels.rule_not_selectable')
                : undefined
        },
        remainingSum() {
            return (
                this.model.budget -
                (this.groupOrderContribution ? this.groupOrderContribution : this.priceSum)
            )
        },
        isStripe() {
            return this.cart.payment_method === this.$cardPayment
        },
        groupOrderContribution() {
            return this.cart.calculation.group_order_employees?.length
                ? this.cart.calculation.group_order_sum_distribution?.['']
                : null
        },
    },
    watch: {
        value: {
            handler(n, o) {
                if (n === null && o !== null && o === this.url) {
                    this.$emit('input', n)
                }
            },
        },
    },
    methods: {
        onInput() {
            this.$emit('input', cloneDeep(this.model))
        },
    },
}
</script>

<template lang="pug">
radio-line.card(:value="value" @input="onInput" :class="{'active': active}" :data="url" :disabled="disabled" :readonly="cart.loading")
    .d-flex.flex-column.mr-4
        span.text-break {{ model.name }}
        .text-73.text-nowrap.line-1 {{ model.budget | toCurrency(model.locale, model.currency_code) }} {{$t('base.available')}}
            span.ml-3(v-if="remainingSum < 0") ({{ remainingSum | toCurrency(model.locale, model.currency_code) }})
    template(v-if="disabled" v-slot:radioIcon)
        icon.text-info.cursor-help(
            icon="fa-solid fa-info-circle"
            size="lg"
            v-tooltip.hover.top.html="information")
    template(v-if="(showExpenseCodes || showGroupSelector || showPaymentExtension) && !disabled" v-slot:extend)
        .px-5.pb-2
            transition(name="fade")
                hr.mb-8.mt-0.border-26(v-if="showExpenseCodes || showGroupSelector || showPaymentExtension")
            box(:value="showPaymentExtension" slim)
                complementary-payment-block.mt-4(:stripe="stripe" id="complementary_payment_method" :remaining-sum="remainingSum")
                hr.my-8.border-26(v-if="showExpenseCodes || showGroupSelector")
            box(:value="showExpenseCodes" slim)
                expense-code-block.mt-4(id="expense_code" :active="showExpenseCodes")                
                hr.my-8.border-26(v-if="!showExpenseCodes || showGroupSelector")
            box(:value="showGroupSelector" slim)
                group-order-block.mt-4(:expense-rule-id="model.id" id="group_info" checkout)


</template>
